import { onScopeDispose, ref, watch } from 'vue';

const OVERLAY_ID = 'dropdown-modal-overlay';
const DROPDOWN_ID = 'order-dropdown';
const DROPDOWN_OFFSET = 8;

export default function useOrderDropdown() {
  const isOrderDropdownOpen = ref(false);
  const dropdownPosition = ref({ top: 0, right: 0 });
  const orderButtonRef = ref<HTMLElement | null>(null);
  let modalOverlay: HTMLElement | null = null;

  const createModalOverlay = () => {
    const overlay = document.createElement('div');
    overlay.id = OVERLAY_ID;
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      z-index: 40;
      background: transparent;
      cursor: default;
    `;
    document.body.appendChild(overlay);
    return overlay;
  };

  const setScrollLocked = (locked: boolean) => {
    const overflow = locked ? 'hidden' : '';
    document.body.style.overflow = overflow;
    document.documentElement.style.overflow = overflow;
    const appElement = document.getElementById('app');
    if (appElement) {
      appElement.style.overflow = overflow;
    }
  };

  const closeDropdown = () => {
    isOrderDropdownOpen.value = false;
  };

  const showModalOverlay = () => {
    if (!modalOverlay) {
      modalOverlay = createModalOverlay();
      modalOverlay.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          closeDropdown();
        },
        { capture: true }
      );
    }
    setScrollLocked(true);
  };

  const hideModalOverlay = () => {
    if (modalOverlay) {
      document.body.removeChild(modalOverlay);
      modalOverlay = null;
    }
    setScrollLocked(false);
  };

  const calculateDropdownPosition = () => {
    if (!orderButtonRef.value) return;

    const rect = orderButtonRef.value.getBoundingClientRect();

    dropdownPosition.value = {
      top: rect.bottom + DROPDOWN_OFFSET,
      right: window.innerWidth - rect.right,
    };
  };

  const handleOrder = () => {
    if (!isOrderDropdownOpen.value) {
      calculateDropdownPosition();
    }
    isOrderDropdownOpen.value = !isOrderDropdownOpen.value;
  };

  watch(isOrderDropdownOpen, (isOpen) => {
    if (isOpen) {
      showModalOverlay();
    } else {
      hideModalOverlay();
    }
  });

  onScopeDispose(() => {
    if (modalOverlay) {
      hideModalOverlay();
    }
  }, true);

  const handleClickOutside = (event: MouseEvent) => {
    if (!isOrderDropdownOpen.value) return;

    const dropdown = document.getElementById(DROPDOWN_ID);
    const target = event.target as HTMLElement;

    if (dropdown && !dropdown.contains(target) && !orderButtonRef.value?.contains(target)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      closeDropdown();
    }
  };

  return {
    isOrderDropdownOpen,
    dropdownPosition,
    orderButtonRef,
    handleOrder,
    closeDropdown,
    handleClickOutside,
    hideModalOverlay,
  };
}
